// Renders one declarative field (mirrors backend template types) — mobile-first client portal styling.
import React, { useEffect, useRef, useState } from 'react';
import { Plus, Trash2, Check, User, Users, Search, ChevronDown, ChevronUp, Pencil, CalendarDays, AlertCircle, MapPin } from 'lucide-react';
import { fieldVisible, fmtDate, fmtMoney } from './rules';

// 16px text stops iOS Safari zooming the page when an input is focused.
export const inputCls = 'w-full rounded-2xl bg-[#F4F4F6] border border-transparent focus:border-[#2E333B] focus:bg-white focus:ring-4 focus:ring-[#2E333B]/10 px-4 text-base text-[#2E333B] placeholder:text-slate-400 outline-none transition min-h-[54px]';
const tap = 'active:scale-[0.985] transition-[transform,background-color,border-color,box-shadow] duration-150';

export function Label({ field, children }) {
  return (
    <label className="block text-[16px] font-bold text-[#2E333B] mb-2.5 font-[Outfit] leading-snug" data-testid={`ob-label-${field.key}`}>
      {children || field.label}
      {field.required ? <span className="text-rose-500 ml-1">*</span> : <span className="text-slate-400 font-medium text-xs ml-2 font-[Manrope]">optional</span>}
      {field.description && <span className="block mt-1 text-sm font-medium text-slate-500 font-[Manrope] leading-relaxed">{field.description}</span>}
    </label>
  );
}

function MultiButtons({ field, value, onChange, testId }) {
  const vals = Array.isArray(value) ? value : [];
  const toggle = (v) => onChange(vals.includes(v) ? vals.filter((x) => x !== v) : [...vals, v]);
  return (
    <div className="grid gap-2.5 grid-cols-1 sm:grid-cols-2" data-testid={testId}>
      {(field.options || []).map((o) => { const active = vals.includes(o.value); return (
        <button key={o.value} type="button" onClick={() => toggle(o.value)} data-testid={`${testId}-${o.value}`} aria-pressed={active}
          className={`${tap} flex items-center gap-3 rounded-2xl border px-4 py-3.5 text-left text-base font-semibold min-h-[56px] ${active ? 'border-[#2E333B] bg-[#2E333B]/5 text-[#2E333B] ring-2 ring-[#2E333B]' : 'border-slate-200 bg-white text-[#2E333B] hover:border-[#2E333B]/50'}`}>
          <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-lg border ${active ? 'border-[#2E333B] bg-[#2E333B] text-white' : 'border-slate-300'}`}>{active && <Check className="h-3.5 w-3.5" />}</span>{o.label}
        </button>); })}
    </div>
  );
}

function OptionButtons({ field, value, onChange, testId, onPick }) {
  const opts = field.options || [];
  const short = opts.length <= 2 && opts.every((o) => (o.label || '').length <= 14);
  return (
    <div className={`grid gap-2.5 ${short ? 'grid-cols-2' : 'grid-cols-1 sm:grid-cols-2'}`} data-testid={testId} role="radiogroup">
      {opts.map((o) => {
        const active = value === o.value;
        return (
          <button key={o.value} type="button" role="radio" aria-checked={active} onClick={() => { onChange(o.value); onPick?.(); }} data-testid={`${testId}-${o.value}`}
            className={`${tap} flex items-center gap-3 rounded-2xl border px-4 py-3.5 text-left text-base font-semibold min-h-[56px] ${
              active ? 'border-[#2E333B] bg-[#2E333B] text-white shadow-md shadow-[#2E333B]/20' : 'border-slate-200 bg-white text-[#2E333B] hover:border-[#2E333B]/50'}`}>
            <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border ${active ? 'border-white/60 bg-white/20' : 'border-slate-300'}`}>
              {active && <Check className="h-3.5 w-3.5" />}
            </span>
            <span className="leading-snug">{o.label}</span>
          </button>
        );
      })}
    </div>
  );
}

// ── Date: DD / MM / YYYY boxes — numeric keypad, auto-advance, no calendar scrolling ──
const pad = (n, l) => String(n).padStart(l, '0');
const parseIso = (iso) => { const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || ''); return m ? { d: m[3], m: m[2], y: m[1] } : { d: '', m: '', y: '' }; };
const validDate = (d, m, y) => { const D = +d, M = +m, Y = +y; if (!(Y >= 1900 && Y <= 2100 && M >= 1 && M <= 12 && D >= 1 && D <= 31)) return null; const dt = new Date(Y, M - 1, D); return dt.getFullYear() === Y && dt.getMonth() === M - 1 && dt.getDate() === D ? dt : null; };
const spanText = (dt) => {
  const now = new Date(); let months = (now.getFullYear() - dt.getFullYear()) * 12 + (now.getMonth() - dt.getMonth()); if (now.getDate() < dt.getDate()) months -= 1;
  const future = months < 0; months = Math.abs(months); const y = Math.floor(months / 12), mo = months % 12;
  const parts = [y ? `${y} yr${y === 1 ? '' : 's'}` : null, mo ? `${mo} mth${mo === 1 ? '' : 's'}` : null].filter(Boolean).join(' ') || 'less than a month';
  return future ? `in ${parts}` : `${parts} ago`;
};
export function DateInput({ field, value, onChange, testId }) {
  const [p, setPState] = useState(() => parseIso(value));
  const pRef = useRef(p);
  const setP = (n) => { pRef.current = n; setPState(n); };
  const refs = [useRef(null), useRef(null), useRef(null)];
  useEffect(() => { const localIso = p.d.length === 2 && p.m.length === 2 && p.y.length === 4 && validDate(p.d, p.m, p.y) ? `${p.y}-${p.m}-${p.d}` : null; if (value && (value || null) !== localIso) setP(parseIso(value)); }, [value]);
  const isDob = /birth|dob/i.test(field.key || '') || /birth/i.test(field.label || '');
  const commit = (n) => {
    setP(n);
    const dt = n.d.length === 2 && n.m.length === 2 && n.y.length === 4 ? validDate(n.d, n.m, n.y) : null;
    const iso = dt ? `${n.y}-${n.m}-${n.d}` : null;
    if (iso !== (value || null)) onChange(iso);
  };
  const set = (k, raw, max, i) => {
    const v = raw.replace(/\D/g, '').slice(0, max);
    commit({ ...pRef.current, [k]: v });
    if (v.length === max && i < 2) refs[i + 1].current?.focus();
  };
  const blurPad = (k, len) => { const cur = pRef.current[k]; if (cur && cur.length < len) commit({ ...pRef.current, [k]: pad(cur, len) }); };
  const onKey = (e, i) => { if (e.key === 'Backspace' && !e.currentTarget.value && i > 0) { e.preventDefault(); refs[i - 1].current?.focus(); } };
  const complete = p.d.length === 2 && p.m.length === 2 && p.y.length === 4;
  const dt = complete ? validDate(p.d, p.m, p.y) : null;
  const partBad = (p.d.length === 2 && (+p.d < 1 || +p.d > 31)) || (p.m.length === 2 && (+p.m < 1 || +p.m > 12)) || (p.y.length === 4 && (+p.y < 1900 || +p.y > 2100));
  const invalid = partBad || (complete && !dt);
  const future = dt && dt > new Date();
  const dobBad = dt && isDob && (future || new Date().getFullYear() - dt.getFullYear() > 110);
  const box = `${inputCls} text-center font-semibold tabular-nums px-2 ${invalid || dobBad ? 'ring-4 ring-rose-100 border-rose-300 bg-rose-50/50' : ''}`;
  let hint = null;
  if (invalid) hint = <><AlertCircle className="h-3.5 w-3.5" /> That date doesn&apos;t look right — check the day and month</>;
  else if (dobBad) hint = <><AlertCircle className="h-3.5 w-3.5" /> Please check your date of birth</>;
  else if (dt) hint = isDob ? <><CalendarDays className="h-3.5 w-3.5" /> {fmtDate(dt.toISOString())} · Age {Math.floor((new Date() - dt) / 31557600000)}</> : <><CalendarDays className="h-3.5 w-3.5" /> {fmtDate(dt.toISOString())} · {spanText(dt)}</>;
  return (
    <div data-testid={testId} data-value={value || ''}>
      <div className="grid grid-cols-[1fr_1fr_1.6fr] gap-2 items-center max-w-sm">
        <input ref={refs[0]} inputMode="numeric" pattern="[0-9]*" autoComplete={isDob ? 'bday-day' : 'off'} placeholder="DD" aria-label="Day" value={p.d} onChange={(e) => set('d', e.target.value, 2, 0)} onBlur={() => blurPad('d', 2)} onKeyDown={(e) => onKey(e, 0)} data-testid={`${testId}-day`} className={box} />
        <input ref={refs[1]} inputMode="numeric" pattern="[0-9]*" autoComplete={isDob ? 'bday-month' : 'off'} placeholder="MM" aria-label="Month" value={p.m} onChange={(e) => set('m', e.target.value, 2, 1)} onBlur={() => blurPad('m', 2)} onKeyDown={(e) => onKey(e, 1)} data-testid={`${testId}-month`} className={box} />
        <input ref={refs[2]} inputMode="numeric" pattern="[0-9]*" autoComplete={isDob ? 'bday-year' : 'off'} placeholder="YYYY" aria-label="Year" value={p.y} onChange={(e) => set('y', e.target.value, 4, 2)} onKeyDown={(e) => onKey(e, 2)} data-testid={`${testId}-year`} className={box} />
      </div>
      <p className={`mt-2 inline-flex items-center gap-1.5 text-xs font-semibold min-h-[16px] ${invalid || dobBad ? 'text-rose-600' : 'text-slate-500'}`} data-testid={`${testId}-hint`}>{hint || <span className="text-slate-400 font-medium">Day / month / year, e.g. 14 03 1986</span>}</p>
    </div>
  );
}

// ── Currency: live thousands separators, decimal keypad, stored as a number ──
const fmtGroup = (digits) => digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
function CurrencyInput({ value, onChange, testId, placeholder }) {
  const [text, setText] = useState(() => (value === null || value === undefined || value === '' ? '' : fmtGroup(String(Math.round(Number(value))))));
  useEffect(() => { const ext = value === null || value === undefined || value === '' ? '' : fmtGroup(String(Math.round(Number(value)))); if (ext.replace(/,/g, '') !== text.replace(/,/g, '')) setText(ext); }, [value]);
  const change = (raw) => { const digits = raw.replace(/[^\d]/g, '').replace(/^0+(?=\d)/, '').slice(0, 12); setText(fmtGroup(digits)); onChange(digits === '' ? null : Number(digits)); };
  return (
    <div className="relative">
      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 font-semibold text-base">$</span>
      <input type="text" inputMode="numeric" pattern="[0-9,]*" value={text} onChange={(e) => change(e.target.value)} data-testid={testId} placeholder={placeholder || '0'} className={`${inputCls} pl-9 tabular-nums font-semibold`} autoComplete="off" />
    </div>
  );
}

// ── Phone: tel keypad + Australian spacing (04xx xxx xxx / 02 xxxx xxxx) ──
const fmtPhone = (raw) => {
  const d = raw.replace(/\D/g, '').slice(0, 10);
  if (d.startsWith('04') || d.startsWith('05') || d.startsWith('13') || d.startsWith('18')) return [d.slice(0, 4), d.slice(4, 7), d.slice(7, 10)].filter(Boolean).join(' ');
  if (d.startsWith('0')) return [d.slice(0, 2), d.slice(2, 6), d.slice(6, 10)].filter(Boolean).join(' ');
  return raw.replace(/[^\d+ ]/g, '').slice(0, 16);
};

// ── Address: type-ahead suggestions (proxied OSM/Photon lookup), free typing always allowed ──
const API = process.env.REACT_APP_BACKEND_URL;
export function AddressInput({ value, onChange, testId }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState(''); // only what the user has typed — never searches a saved value on load
  const boxRef = useRef(null);
  useEffect(() => {
    const q = query.trim();
    if (q.length < 4) { setItems([]); setOpen(false); return; }
    const ctrl = new AbortController(); setLoading(true);
    const t = setTimeout(async () => {
      try { const r = await fetch(`${API}/api/id-verify/address-suggest?q=${encodeURIComponent(q)}`, { signal: ctrl.signal }); const d = r.ok ? await r.json() : []; setItems(Array.isArray(d) ? d.slice(0, 6) : []); setOpen(true); setActive(-1); }
      catch { /* typing continues to work without suggestions */ }
      finally { setLoading(false); }
    }, 450);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [query]);
  useEffect(() => { const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); }; document.addEventListener('pointerdown', h); return () => document.removeEventListener('pointerdown', h); }, []);
  const pick = (it) => { onChange(it.display_name); setQuery(''); setItems([]); setOpen(false); };
  const onKey = (e) => {
    if (!open || !items.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(items.length - 1, a + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(0, a - 1)); }
    else if (e.key === 'Enter' && active >= 0) { e.preventDefault(); pick(items[active]); }
    else if (e.key === 'Escape') setOpen(false);
  };
  const show = open && (items.length > 0 || (!loading && query.trim().length >= 4));
  return (
    <div className="relative" ref={boxRef}>
      <Search className="pointer-events-none absolute left-4 top-[27px] -translate-y-1/2 h-4 w-4 text-slate-500" />
      {loading && <span className="pointer-events-none absolute right-4 top-[27px] -translate-y-1/2 h-4 w-4 rounded-full border-2 border-slate-300 border-t-[#2E333B] animate-spin" />}
      <input type="text" value={value ?? ''} onChange={(e) => { onChange(e.target.value); setQuery(e.target.value); }} onFocus={() => items.length && setOpen(true)} onKeyDown={onKey} data-testid={testId} className={`${inputCls} pl-11 pr-10`} placeholder="Start typing your address…" autoComplete="off" autoCapitalize="words" enterKeyHint="done" role="combobox" aria-expanded={show} aria-autocomplete="list" aria-controls={`${testId}-list`} />
      {show && (
        <ul id={`${testId}-list`} role="listbox" data-testid={`${testId}-suggestions`} className="absolute left-0 right-0 z-30 mt-1.5 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl shadow-slate-900/10 animate-in fade-in slide-in-from-top-1 duration-150">
          {items.map((it, i) => (
            <li key={it.place_id || i} role="option" aria-selected={active === i}>
              <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => pick(it)} onMouseEnter={() => setActive(i)} data-testid={`${testId}-suggestion-${i}`}
                className={`flex w-full items-start gap-3 px-4 py-3 text-left min-h-[52px] ${active === i ? 'bg-[#2E333B]/5' : ''}`}>
                <MapPin className="h-4 w-4 mt-0.5 shrink-0 text-[#2E333B]" />
                <span className="min-w-0"><span className="block text-[15px] font-semibold text-[#2E333B] leading-snug">{it.line1 || it.display_name}</span>{it.line1 && <span className="block text-xs text-slate-500">{[it.suburb, it.state, it.postcode].filter(Boolean).join(' ')}</span>}</span>
              </button>
            </li>
          ))}
          <li className="px-4 py-2.5 text-[11px] text-slate-500 bg-[#F8F8FB] border-t border-slate-100" data-testid={`${testId}-manual-hint`}>{items.length ? 'Not listed? Just keep typing your full address.' : 'No matches yet — keep typing, or enter your full address manually.'}</li>
        </ul>
      )}
    </div>
  );
}

export function FieldInput({ field, value, onChange, ctx, local, testId, errors, onPick }) {
  const tid = testId || `ob-field-${field.key}`;
  switch (field.type) {
    case 'yesno':
      return <OptionButtons field={{ ...field, options: field.options }} value={value} onChange={onChange} testId={tid} onPick={onPick} />;
    case 'multi':
      return <MultiButtons field={field} value={value} onChange={onChange} testId={tid} />;
    case 'checkbox':
      return (
        <button type="button" onClick={() => onChange(!value)} data-testid={tid} aria-pressed={!!value}
          className={`${tap} flex w-full items-center gap-3 rounded-2xl border px-4 py-3.5 text-left text-base font-semibold min-h-[56px] ${value ? 'border-[#2E333B] bg-[#2E333B]/5 text-[#2E333B]' : 'border-slate-200 bg-white text-[#2E333B] hover:border-[#2E333B]/50'}`}>
          <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-lg border ${value ? 'border-[#2E333B] bg-[#2E333B] text-white' : 'border-slate-300'}`}>{value && <Check className="h-3.5 w-3.5" />}</span>{field.placeholder || 'Yes, I confirm'}
        </button>
      );
    case 'select':
      if ((field.options || []).length <= 4 && field.display !== 'dropdown') return <OptionButtons field={field} value={value} onChange={onChange} testId={tid} onPick={onPick} />;
      return (
        <div className="relative">
          <select value={value ?? ''} onChange={(e) => { onChange(e.target.value || null); if (e.target.value) onPick?.(); }} data-testid={tid} className={`${inputCls} appearance-none pr-11 font-semibold`}>
            <option value="">{field.placeholder || 'Select an option…'}</option>
            {(field.options || []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <ChevronDown className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-500" />
        </div>
      );
    case 'currency':
      return <CurrencyInput value={value} onChange={onChange} testId={tid} placeholder={field.placeholder} />;
    case 'number': {
      const pct = /pct|percent/i.test(field.key || '');
      return (
        <div className="relative">
          <input type="text" inputMode={pct ? 'numeric' : 'decimal'} pattern="[0-9.]*" value={value ?? ''} onChange={(e) => { const t = e.target.value.replace(/[^\d.]/g, ''); onChange(t === '' ? null : Number(t)); }} data-testid={tid} className={`${inputCls} tabular-nums font-semibold ${pct ? 'pr-10 max-w-[180px]' : ''}`} placeholder={field.placeholder || '0'} autoComplete="off" />
          {pct && <span className="pointer-events-none absolute left-[140px] top-1/2 -translate-y-1/2 text-slate-500 font-semibold">%</span>}
        </div>
      );
    }
    case 'date':
      return <DateInput field={field} value={value} onChange={onChange} testId={tid} />;
    case 'address':
      return <AddressInput value={value} onChange={onChange} testId={tid} />;
    case 'email':
      return <input type="email" inputMode="email" autoComplete="email" autoCapitalize="none" autoCorrect="off" spellCheck={false} enterKeyHint="next" value={value ?? ''} onChange={(e) => onChange(e.target.value.trim())} data-testid={tid} className={inputCls} placeholder="name@example.com" />;
    case 'phone':
      return <input type="tel" inputMode="tel" autoComplete="tel" enterKeyHint="next" value={value ?? ''} onChange={(e) => onChange(fmtPhone(e.target.value))} data-testid={tid} className={`${inputCls} tabular-nums`} placeholder="04xx xxx xxx" />;
    case 'textarea':
      return <textarea rows={3} value={value ?? ''} onChange={(e) => onChange(e.target.value)} data-testid={tid} className={`${inputCls} py-3.5 resize-y min-h-[104px]`} placeholder={field.placeholder || 'Type your answer here…'} />;
    case 'repeat':
      return <RepeatGroup field={field} value={value} onChange={onChange} ctx={ctx} testId={tid} errors={errors} />;
    default: {
      const nameish = /name/i.test(field.key || '');
      return <input type="text" value={value ?? ''} onChange={(e) => onChange(e.target.value)} data-testid={tid} className={inputCls} placeholder={field.placeholder || 'Type your answer here…'} autoCapitalize={nameish ? 'words' : 'sentences'} autoComplete={/first_name/.test(field.key) ? 'given-name' : /last_name|surname/.test(field.key) ? 'family-name' : 'off'} enterKeyHint="next" />;
    }
  }
}

// One-line summary of a filled repeat row, e.g. "ANZ credit card · $5,000 limit"
const summarise = (field, row) => {
  const parts = [];
  for (const sub of field.fields) {
    const v = row[sub.key]; if (v === undefined || v === null || v === '' || sub.type === 'yesno' || sub.type === 'checkbox') continue;
    if (sub.options) parts.push(sub.options.find((o) => o.value === v)?.label || String(v));
    else if (sub.type === 'currency') parts.push(fmtMoney(v));
    else if (sub.type === 'date') parts.push(fmtDate(v));
    else if (typeof v !== 'object') parts.push(String(v));
    if (parts.length === 3) break;
  }
  return parts.join(' · ');
};

function RepeatGroup({ field, value, onChange, ctx, testId, errors = [] }) {
  const rows = Array.isArray(value) ? value : [];
  const [openIdx, setOpenIdx] = useState(() => (rows.length && Object.keys(rows[rows.length - 1] || {}).length === 0 ? rows.length - 1 : null));
  const update = (i, k, v) => onChange(rows.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  const add = () => { onChange([...rows, {}]); setOpenIdx(rows.length); };
  const remove = (i) => { onChange(rows.filter((_, idx) => idx !== i)); setOpenIdx(null); };
  const rowHasError = (i) => errors.some((e) => e.startsWith(`${field.key}[${i}]`));
  return (
    <div className="space-y-3" data-testid={testId}>
      {rows.map((row, i) => {
        const open = openIdx === i || rowHasError(i);
        const summary = summarise(field, row);
        return (
          <div key={i} className={`rounded-2xl border bg-white overflow-hidden transition-colors ${rowHasError(i) ? 'border-rose-300' : open ? 'border-[#2E333B]/40 shadow-sm' : 'border-slate-200'}`} data-testid={`${testId}-row-${i}`} data-open={open || undefined}>
            <div className="flex items-center gap-3 px-4 py-3">
              <button type="button" onClick={() => setOpenIdx(open ? null : i)} data-testid={`${testId}-toggle-${i}`} className="flex flex-1 min-w-0 items-center gap-3 text-left min-h-[40px]">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#F4F4F6] text-[11px] font-black text-[#2E333B]">{i + 1}</span>
                <span className="min-w-0 flex-1">
                  <span className="block text-[11px] font-black uppercase tracking-[0.14em] text-slate-500">{field.label} {i + 1}</span>
                  {!open && <span className="block text-sm font-semibold text-[#2E333B] truncate" data-testid={`${testId}-summary-${i}`}>{summary || 'Tap to complete'}</span>}
                </span>
                {open ? <ChevronUp className="h-4 w-4 text-slate-400 shrink-0" /> : <Pencil className="h-4 w-4 text-slate-400 shrink-0" />}
              </button>
              <button type="button" onClick={() => remove(i)} data-testid={`${testId}-remove-${i}`} aria-label="Remove" className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-rose-600 hover:bg-rose-50"><Trash2 className="h-4 w-4" /></button>
            </div>
            {open && (
              <div className="grid gap-5 sm:grid-cols-2 border-t border-slate-100 px-4 pt-4 pb-5 animate-in fade-in duration-200">
                {field.fields.filter((sub) => fieldVisible(sub, ctx, row)).map((sub) => { const err = errors.includes(`${field.key}[${i}].${sub.key}`); return (
                  <div key={sub.key} id={`ob-q-${field.key}-${i}-${sub.key}`} data-error={err || undefined} className={`rounded-xl transition-shadow ${sub.type === 'address' || sub.type === 'textarea' || sub.type === 'date' ? 'sm:col-span-2' : ''} ${err ? 'ob-attention ring-2 ring-rose-400 ring-offset-4 ring-offset-white bg-rose-50/60' : ''}`}>
                    <Label field={sub}>{sub.label}{err && <span className="ml-2 rounded-full bg-rose-600 px-2 py-0.5 text-[10px] font-black uppercase tracking-wider text-white align-middle">Required</span>}</Label>
                    <FieldInput field={sub} value={row[sub.key]} onChange={(v) => update(i, sub.key, v)} ctx={ctx} local={row} testId={`${testId}-${i}-${sub.key}`} />
                    {err && <p className="mt-1.5 text-xs font-semibold text-rose-600" data-testid={`ob-error-${field.key}-${i}-${sub.key}`}>This answer is required</p>}
                  </div>); })}
                <button type="button" onClick={() => setOpenIdx(null)} data-testid={`${testId}-done-${i}`} className={`${tap} sm:col-span-2 inline-flex items-center justify-center gap-2 rounded-2xl border border-[#2E333B] bg-white px-4 py-3 text-sm font-bold text-[#2E333B] min-h-[48px]`}><Check className="h-4 w-4" /> Done with this {field.label.toLowerCase().replace(/s$/, '')}</button>
              </div>
            )}
          </div>
        );
      })}
      <button type="button" onClick={add} data-testid={`${testId}-add`}
        className={`${tap} w-full inline-flex items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-[#2E333B]/30 bg-[#2E333B]/[0.03] text-[#2E333B] font-bold py-3.5 text-base min-h-[56px] hover:border-[#2E333B]/60`}>
        <Plus className="h-5 w-5" /> {field.add_label || `Add ${field.label.toLowerCase()}`}
      </button>
    </div>
  );
}

export function PrefilledBadge({ meta }) {
  if (!meta || meta.status !== 'prefilled' || meta.value === undefined || meta.value === null || meta.value === '') return null;
  return (
    <p className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-amber-50 border border-amber-200 px-2.5 py-1 text-[11px] font-semibold text-amber-800" data-testid="ob-prefilled-badge">
      We have this on file · {fmtDate(meta.updated_at)} — please check it
    </p>
  );
}

export const ApplicantIcon = ({ joint }) => (joint ? <Users className="h-4 w-4" /> : <User className="h-4 w-4" />);
